import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { graphqlApi } from '../../services/graphql';
import { useAuthStore } from '../../store/authStore';
import { Spinner } from '../common/Spinner';

export interface WithdrawAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  influencerId?: string;
}

/** Phrase the user must type to confirm withdrawal (step 2). */
const CONFIRM_PHRASE = '매치고안녕';
const ONGOING_STATUSES = ['requested', 'accepted'];

const WithdrawAccountModal: React.FC<WithdrawAccountModalProps> = ({
  isOpen,
  onClose,
  userId,
  influencerId,
}) => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const [step, setStep] = useState(1);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [ongoingCount, setOngoingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [agree, setAgree] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [feedback, setFeedback] = useState('');
  const [phrase, setPhrase] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = useMemo(
    () => [
      t('myProfile.withdraw.reasons.0', '서비스 사용이 불편하거나 복잡해요'),
      t('myProfile.withdraw.reasons.1', '서비스를 더 이상 사용하지 않아요'),
      t('myProfile.withdraw.reasons.2', '제가 추구하는 콘텐츠의 방향성과 달라요'),
      t('myProfile.withdraw.reasons.3', '제휴/캠페인 수익이 낮거나 정산 과정이 복잡해요'),
      t('myProfile.withdraw.reasons.4', '다른 서비스에서 더 나은 혜택을 제공해요'),
      t('myProfile.withdraw.reasons.5', '개인정보 보안이 우려돼요'),
      t('myProfile.withdraw.reasons.6', '기타'),
    ],
    [t],
  );

  // Reset + load match status whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setAgree(false);
    setPicked([]);
    setFeedback('');
    setPhrase('');
    setSubmitting(false);

    let cancelled = false;
    (async () => {
      setLoadingStatus(true);
      try {
        if (influencerId) {
          const res = await graphqlApi.getMatches({ influencerId });
          const matches: any[] = res?.success ? res.data?.matches || [] : [];
          if (!cancelled) {
            setOngoingCount(matches.filter((m) => ONGOING_STATUSES.includes(m.status)).length);
            setCompletedCount(matches.filter((m) => m.status === 'completed').length);
          }
        } else {
          if (!cancelled) {
            setOngoingCount(0);
            setCompletedCount(0);
          }
        }
      } catch {
        if (!cancelled) {
          setOngoingCount(0);
          setCompletedCount(0);
        }
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, influencerId]);

  if (!isOpen) return null;

  const hasOngoing = ongoingCount > 0;
  const canNext = agree && !hasOngoing && !loadingStatus;
  const canSubmit =
    !hasOngoing && agree && picked.length > 0 && phrase.trim() === CONFIRM_PHRASE && !submitting;

  const toggleReason = (r: string) =>
    setPicked((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await graphqlApi.withdrawAccount(userId, picked, feedback.trim() || undefined);
      if (res.success) {
        toast.success(res.message || t('myProfile.withdraw.success', '회원 탈퇴가 완료되었습니다.'));
        await logout();
        navigate('/login');
      } else if (res.blocked) {
        setOngoingCount(res.ongoingCount ?? ongoingCount);
        setStep(1);
        toast.error(res.error || t('myProfile.withdraw.blocked', '진행 중인 매칭이 있어 탈퇴할 수 없습니다.'));
      } else {
        toast.error(res.error || t('myProfile.withdraw.failed', '회원 탈퇴에 실패했습니다.'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('myProfile.withdraw.failed', '회원 탈퇴에 실패했습니다.'));
    } finally {
      setSubmitting(false);
    }
  };

  const noticeItems: string[] = [
    t('myProfile.withdraw.notice.0', '회원 탈퇴 시 모든 개인정보 및 데이터(프로필, 작성한 콘텐츠, 매칭·추천 내역 등)는 삭제됩니다.'),
    t('myProfile.withdraw.notice.1', '진행 중인 매칭(계약 체결 후 완료 이전 캠페인)이 있는 경우 탈퇴가 제한되며, 매칭이 취소 또는 종료된 후 신청할 수 있습니다.'),
    t('myProfile.withdraw.notice.2', '완료된 매칭의 콘텐츠 성과 리포트는 해당 캠페인 계약 기간 동안 유지되며, 계약 종료 후에는 복구할 수 없습니다.'),
    t('myProfile.withdraw.notice.3', '정산 신청을 완료한 지급 대기 리워드는 등록 계좌로 지급됩니다.'),
    t('myProfile.withdraw.notice.4', '정산을 신청하지 않은 리워드 잔액은 지급되지 않고 삭제됩니다.'),
    t('myProfile.withdraw.notice.5', '탈퇴 후 30일간 동일 계정으로 재가입이 불가능합니다.'),
    t('myProfile.withdraw.notice.6', '탈퇴 이후 궁금하신 사항은 고객센터(help@matchgo.ai)로 문의해 주세요.'),
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F1E3D]/40 p-5"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-[520px] max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <h2 className="text-[16.5px] font-bold text-[#0F1E3D] tracking-tight">
            {t('myProfile.withdraw.title', '회원탈퇴')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex items-center justify-center"
            aria-label={t('myProfile.withdraw.close', '닫기')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-2 px-6 pt-4 font-mono text-[11px] text-gray-400">
          <span className={`inline-flex items-center gap-1.5 ${step === 1 ? 'text-[#0F1E3D] font-semibold' : ''}`}>
            <span className={`w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold ${step === 1 ? 'bg-[#0F1E3D] text-white' : 'bg-gray-200 text-gray-500'}`}>1</span>
            {t('myProfile.withdraw.step1', '안내 확인')}
          </span>
          <span className="flex-1 h-px bg-gray-200" />
          <span className={`inline-flex items-center gap-1.5 ${step === 2 ? 'text-[#0F1E3D] font-semibold' : ''}`}>
            <span className={`w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold ${step === 2 ? 'bg-[#0F1E3D] text-white' : 'bg-gray-200 text-gray-500'}`}>2</span>
            {t('myProfile.withdraw.step2', '사유 입력')}
          </span>
        </div>

        {/* Body */}
        {loadingStatus ? (
          <div className="py-16 flex justify-center"><Spinner size="medium" /></div>
        ) : step === 1 ? (
          <div className="px-6 py-5 space-y-5">
            {/* Status panel */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-[#0F1E3D] text-white text-[11px] font-bold tracking-wider uppercase px-3.5 py-2.5">
                {t('myProfile.withdraw.statusHead', '매칭 처리 현황')}
              </div>
              <div className="grid grid-cols-2 gap-px bg-gray-100">
                <div className="bg-white px-3.5 py-3">
                  <div className="text-[12px] text-gray-500">{t('myProfile.withdraw.ongoing', '진행 중인 매칭')}</div>
                  <div className="mt-1 font-mono font-semibold text-[#0F1E3D]">
                    {ongoingCount}{t('myProfile.withdraw.unit', '건')}
                  </div>
                </div>
                <div className="bg-white px-3.5 py-3">
                  <div className="text-[12px] text-gray-500">{t('myProfile.withdraw.completed', '완료된 매칭')}</div>
                  <div className="mt-1 font-mono font-semibold text-[#0F1E3D]">
                    {completedCount}{t('myProfile.withdraw.unit', '건')}
                  </div>
                </div>
              </div>
            </div>

            {/* Notice list */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[13.5px] font-bold text-[#0F1E3D]">{t('myProfile.withdraw.noticeTitle', '안내사항')}</span>
                <span className="font-mono text-[9.5px] font-bold text-[#F25151] bg-[#FFE4E4] rounded px-1.5 py-0.5">{t('myProfile.withdraw.required', '필수 확인')}</span>
              </div>
              <ul className="bg-[#F7F8FB] border border-gray-200 rounded-lg p-4 space-y-2">
                {noticeItems.map((item, i) => (
                  <li key={i} className="relative pl-3.5 text-[12px] leading-relaxed text-[#1F3266]">
                    <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-gray-400" />
                    {item}
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-2 mt-3 text-[12.5px] text-[#0F1E3D] cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="w-4 h-4 accent-[#2D5BFF]"
                />
                {t('myProfile.withdraw.agree', '위 안내 내용을 모두 확인하였으며, 이에 동의합니다. (필수)')}
              </label>
            </div>

            {hasOngoing && (
              <div className="flex gap-2.5 bg-[#FFE4E4] rounded-lg px-4 py-3">
                <span className="font-bold text-[11px] text-[#F25151] shrink-0 pt-0.5">{t('myProfile.withdraw.limitLabel', '탈퇴 제한')}</span>
                <span className="text-[12px] text-[#1F3266] leading-relaxed">
                  {t('myProfile.withdraw.limitText', { count: ongoingCount, defaultValue: '진행 중인 매칭 {{count}}건이 있어 지금은 탈퇴할 수 없습니다. 매칭이 취소 또는 종료된 후 다시 시도해 주세요.' })}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Reasons */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[13.5px] font-bold text-[#0F1E3D]">{t('myProfile.withdraw.reasonTitle', '탈퇴하시려는 이유를 알려주세요')}</span>
                <span className="font-mono text-[9.5px] font-bold text-[#F25151] bg-[#FFE4E4] rounded px-1.5 py-0.5">{t('myProfile.withdraw.requiredMulti', '필수 · 중복선택')}</span>
              </div>
              <div className="border border-gray-200 rounded-lg p-1.5">
                {reasons.map((r) => (
                  <label key={r} className="flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-gray-50 cursor-pointer text-[13px] text-[#0F1E3D]">
                    <input
                      type="checkbox"
                      checked={picked.includes(r)}
                      onChange={() => toggleReason(r)}
                      className="w-4 h-4 accent-[#2D5BFF]"
                    />
                    {r}
                  </label>
                ))}
              </div>
            </div>

            {/* Feedback */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[13.5px] font-bold text-[#0F1E3D]">{t('myProfile.withdraw.feedbackTitle', '불편하셨던 점이 있으신가요?')}</span>
                <span className="font-mono text-[9.5px] font-semibold text-gray-500 bg-[#F7F8FB] border border-gray-200 rounded px-1.5 py-0.5">{t('myProfile.withdraw.optional', '선택')}</span>
              </div>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value.slice(0, 1000))}
                placeholder={t('myProfile.withdraw.feedbackPlaceholder', '불편하셨던 점을 구체적으로 알려주시면 서비스 개선에 큰 도움이 됩니다.')}
                className="w-full min-h-[96px] resize-y text-[13px] leading-relaxed px-3 py-3 border border-gray-200 rounded-lg text-[#0F1E3D] focus:outline-none focus:border-[#2D5BFF] focus:ring-2 focus:ring-[#E6EDFF]"
              />
              <div className="text-right font-mono text-[10.5px] text-gray-400 mt-1">{feedback.length}/1000</div>
            </div>

            {/* Confirm phrase */}
            <div>
              <p className="text-[13.5px] font-bold text-[#0F1E3D] leading-relaxed mb-2">
                {t('myProfile.withdraw.confirmLead', '마지막으로 회원탈퇴를 위해')}{' '}
                <span className="font-mono">"<b className="text-[#2D5BFF]">{CONFIRM_PHRASE}</b>"</span>
                {t('myProfile.withdraw.confirmLeadTail', '을 입력해 주세요')}
              </p>
              <input
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="w-full text-[14px] px-3.5 py-3 border border-gray-200 rounded-lg text-[#0F1E3D] focus:outline-none focus:border-[#2D5BFF] focus:ring-2 focus:ring-[#E6EDFF]"
              />
            </div>
          </div>
        )}

        {/* Footer */}
        {!loadingStatus && (
          <div className="grid grid-cols-2 border-t border-gray-200">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="py-3.5 text-[14px] font-bold text-[#4A5D8C] bg-[#F7F8FB] hover:bg-gray-100"
                >
                  {t('myProfile.withdraw.cancel', '취소')}
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => setStep(2)}
                  className="py-3.5 text-[14px] font-bold text-white bg-[#0F1E3D] hover:bg-[#2D5BFF] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {t('myProfile.withdraw.next', '다음')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="py-3.5 text-[14px] font-bold text-[#4A5D8C] bg-[#F7F8FB] hover:bg-gray-100"
                >
                  {t('myProfile.withdraw.prev', '이전')}
                </button>
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  className="py-3.5 text-[14px] font-bold text-white bg-[#F25151] hover:bg-[#d83f3f] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {submitting && <Spinner size="small" />}
                  {t('myProfile.withdraw.submit', '회원 탈퇴하기')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawAccountModal;
